import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, IconButton, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import AuroraTargetTemperatureInput from './AuroraTargetTemperatureInput'
import { formatTemperature, formatTemperatureDelta } from '../utils/string'

const MAX_TARGET_TEMPERATURE = 100

const MIN_TARGET_TEMPERATURE = 0
interface AuroraTemperatureControlProps {
  value: number | null
  loading?: boolean
  secondarySupplyTemperature?: number | null
  minTemperatureLimit?: number | null
  onSubmit: (value: number) => Promise<void>
}

export default function AuroraTargetTemperatureControl({
  value: targetTemperature,
  loading = false,
  secondarySupplyTemperature = null,
  minTemperatureLimit = null,
  onSubmit
}: AuroraTemperatureControlProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The theme hook.
   */
  const theme = useTheme()

  /**
   * The new target temperature.
   */
  const [newTargetTemperature, setNewTargetTemperature] = useState<number | null>(null)

  const temperatureDelta =
    targetTemperature !== null && secondarySupplyTemperature !== null
      ? secondarySupplyTemperature - targetTemperature
      : null

  const clampCeil = (value: number) => {
    const clamped = Math.min(value, MAX_TARGET_TEMPERATURE)

    return Math.max(clamped, minTemperatureLimit ?? MIN_TARGET_TEMPERATURE)
  }

  const clampFloor = (value: number) => {
    const clamped = Math.max(value, minTemperatureLimit ?? MIN_TARGET_TEMPERATURE)

    return Math.min(clamped, MAX_TARGET_TEMPERATURE)
  }

  /**
   * Increments the target temperature by 0.5 degrees.
   */
  function handleIncrement() {
    setNewTargetTemperature((prev) => {
      const value = prev !== null ? prev : targetTemperature

      if (value === null) {
        return null
      }

      return clampCeil(Math.ceil(value * 2) / 2 + 0.5)
    })
  }

  /**
   * Decrements the target temperature by 0.5 degrees.
   */
  function handleDecrement() {
    setNewTargetTemperature((prev) => {
      const value = prev !== null ? prev : targetTemperature

      if (value === null) {
        return null
      }

      return clampFloor(Math.floor(value * 2) / 2 - 0.5)
    })
  }

  /**
   * Update the target temperature.
   */
  async function handleConfirm() {
    if (newTargetTemperature === null) {
      return
    }

    try {
      await onSubmit(newTargetTemperature)

      setNewTargetTemperature(null)
    } catch {
      // Ignore the error.
    }
  }

  /**
   * Reset the new target temperature.
   */
  function handleReset() {
    setNewTargetTemperature(null)
  }

  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="center"
      gap={2}
      sx={{
        p: 4,
        borderRadius: 2,
        background: theme.palette.background.default
      }}
    >
      <Typography color="text.secondary">{t('targetTemperature')}</Typography>

      <Stack direction="column" justifyContent="center" alignItems="center" sx={{ mt: 1 }} gap={2}>
        <Stack direction="row" alignItems="center" gap={3}>
          {targetTemperature !== null && (
            <IconButton
              color={newTargetTemperature !== null ? 'primary' : 'inherit'}
              disabled={loading}
              size="small"
              sx={{ border: '1px solid' }}
              onClick={handleDecrement}
            >
              <Remove />
            </IconButton>
          )}

          <Stack direction="column" justifyContent="center" alignItems="center" gap={1}>
            {newTargetTemperature !== null ? (
              <AuroraTargetTemperatureInput
                value={newTargetTemperature}
                loading={loading}
                onChange={setNewTargetTemperature}
                onSubmit={handleConfirm}
              />
            ) : targetTemperature !== null ? (
              <Typography variant="h2" color="text.primary">
                {formatTemperature(targetTemperature)}
              </Typography>
            ) : (
              <Skeleton variant="text" width={80} height={48} />
            )}

            {temperatureDelta !== null && (
              <Typography color="text.secondary">
                {`ΔT: ${formatTemperatureDelta(temperatureDelta)}`}
              </Typography>
            )}
          </Stack>

          {targetTemperature !== null && (
            <IconButton
              color={newTargetTemperature !== null ? 'primary' : 'inherit'}
              disabled={loading}
              size="small"
              sx={{
                border: '2px solid'
              }}
              onClick={handleIncrement}
            >
              <Add />
            </IconButton>
          )}
        </Stack>

        {newTargetTemperature !== null && (
          <Stack direction="row" justifyContent="center" alignItems="center" gap={2}>
            <LoadingButton
              variant="outlined"
              color="primary"
              size="small"
              loading={loading}
              onClick={handleConfirm}
            >
              {t('actions:confirm')}
            </LoadingButton>

            <Button
              variant="text"
              color="inherit"
              size="small"
              disabled={loading}
              onClick={handleReset}
            >
              {t('actions:cancel')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
